import { useCallback, useEffect, useRef, useState } from "react";

import { isScoutOptimisticConversationId } from "./scoutConversationIds";
import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "./scoutWriteMode";

type UseScoutWriteModeInput = {
  conversationId: string | undefined;
  conversationWriteMode: ScoutWriteModeGql | undefined;
  /**
   * When true, reset the control whenever `conversationId` becomes undefined
   * (e.g. popover session reset). When false, preserve local state across the
   * undefined→id transition after the user's first message on the main page.
   */
  resetWhenConversationClears?: boolean;
};

export function useScoutWriteMode(input: UseScoutWriteModeInput) {
  const { conversationId, conversationWriteMode, resetWhenConversationClears = false } = input;
  const [writeMode, setWriteMode] = useState<ScoutWriteModeGql>(SCOUT_WRITE_MODE_GQL.OFF);
  const userTouchedRef = useRef(false);
  const lastConversationIdRef = useRef<string | undefined>(conversationId);

  useEffect(() => {
    const previousConversationId = lastConversationIdRef.current;
    lastConversationIdRef.current = conversationId;

    if (resetWhenConversationClears && !conversationId) {
      userTouchedRef.current = false;
      setWriteMode(SCOUT_WRITE_MODE_GQL.OFF);
      return;
    }

    if (!previousConversationId) {
      return;
    }

    if (previousConversationId === conversationId) {
      return;
    }

    // Same in-flight thread: optimistic temp id → persisted uuid. Keep the
    // user's Ask/Auto selection until store/history writeMode catches up.
    if (
      userTouchedRef.current &&
      isScoutOptimisticConversationId(previousConversationId) &&
      conversationId !== undefined &&
      !isScoutOptimisticConversationId(conversationId)
    ) {
      return;
    }

    userTouchedRef.current = false;
    setWriteMode(conversationWriteMode ?? SCOUT_WRITE_MODE_GQL.OFF);
  }, [conversationWriteMode, conversationId, resetWhenConversationClears]);

  useEffect(() => {
    if (!userTouchedRef.current) {
      setWriteMode(conversationWriteMode ?? SCOUT_WRITE_MODE_GQL.OFF);
    }
  }, [conversationWriteMode]);

  const onWriteModeChange = useCallback((next: ScoutWriteModeGql) => {
    userTouchedRef.current = true;
    setWriteMode(next);
  }, []);

  return { writeMode, onWriteModeChange };
}
